import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import EnhancedSection from './components/EnhancedSection';
import { Badge, RadioButton } from '@shopline/dashboard-ui';
import Like from './components/Like';
import { Actions, useConfigurationForm } from 'contexts/ConfigurationForm';
import usePermissions from 'hooks/usePermissions';
import { SectionId } from './constants';

const BadgeWrapper = styled.div`
  margin-left: 8px;

  // badge
  > div {
    padding: 3px 8px;
  }

  // badge text
  > div > span {
    color: ${({ theme }) => theme.colors.GREEN_600};
    font-weight: 400;
    font-size: 13px;
    line-height: 16px;
    padding: 0px;
  }
`;

const RadioBtns = styled.div`
  display: flex;
  flex-direction: column;

  > label {
    display: flex;
    align-items: center;

    // radio circle
    > div:first-of-type {
      margin: 0;
    }

    // label text
    > div > span {
      max-width: 100%;
      display: flex;
      align-items: center;
    }
  }

  > p {
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

RadioBtns.HelpText = styled.p`
  margin: 4px 0 0 24px;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.INK_500};
`;

const ProductApiInputs = (props?: { onChange?: () => void }) => {
  const { t } = useTranslation(['main', 'common']);
  const canEdit = usePermissions('merchant', 'edit');
  const {
    state: {
      editingState: { productApiEnabled },
    },
    dispatch,
  } = useConfigurationForm();
  const setProductApiEnabled = (productApiEnabled: boolean) => {
    dispatch(Actions.setProductApiEnabled(productApiEnabled));
    props?.onChange?.();
  };

  return (
    <EnhancedSection
      title={t('Connect Merchant API')}
      id={SectionId.PRODUCT_API_ENABLED}
    >
      <EnhancedSection.Description>
        {t(
          'Through a Merchant API connection, your product data will be automatically uploaded to Google Merchant Center, allowing them to be accessible in Google Ads, YouTube Live Shopping, and other Google features.',
        )}
      </EnhancedSection.Description>
      <RadioBtns>
        <RadioButton
          label={
            (
              <>
                {t('Connect Merchant API Label')}
                <BadgeWrapper>
                  <Badge.Outline
                    color="GREEN_600"
                    fontType="Caption"
                    label={t('Recommended')}
                    Icon={Like}
                  />
                </BadgeWrapper>
              </>
            ) as any
          }
          checked={productApiEnabled}
          onChange={() => {
            setProductApiEnabled(!productApiEnabled);
          }}
          disabled={!canEdit}
        />
        <RadioBtns.HelpText>
          {t(
            'Recommended for real-time product sync, hidden product setting, and simplified maintenance.',
          )}
        </RadioBtns.HelpText>
        <RadioButton
          label={t('Do not connect Merchant API')}
          checked={!productApiEnabled}
          onChange={() => {
            setProductApiEnabled(!productApiEnabled);
          }}
          disabled={!canEdit}
        />
        <RadioBtns.HelpText>
          {t(
            'Product data will not sync automatically. You must manually update the Product Feed in Google Merchant Center. This may affect product approval and exposure.',
          )}
        </RadioBtns.HelpText>
      </RadioBtns>
    </EnhancedSection>
  );
};

export default ProductApiInputs;
